import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Group, Tabs, Text } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { useServerApi } from "../hooks/userServerApi";
import AccountingReportFilters from "./accounting/AccountingReportFilters";
import ProfitAndLossView from "./accounting/ProfitAndLossView";
import BalanceSheetView from "./accounting/BalanceSheetView";
import {
  defaultPeriodEndDate,
  downloadBalanceSheetCsv,
  downloadProfitAndLossCsv,
  formatDateLabel,
  hasBalanceSheetActivity,
  hasPnlActivity,
} from "./accounting/financialStatementsUtils";

const ReportFinancialStatements = () => {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const [pnl, setPnl] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [fiscalPeriodId, setFiscalPeriodId] = useState(null);
  const [periodName, setPeriodName] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [bsAsOfDate, setBsAsOfDate] = useState(null);
  const [activeTab, setActiveTab] = useState("pnl");
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const bsAsOfOverrideRef = useRef(false);

  const loadReports = async () => {
    setLoading(true);
    try {
      const pnlQuery = { fiscalPeriodId };
      const bsQuery = { fiscalPeriodId };
      if (startDate) pnlQuery.startDate = startDate.toISOString();
      if (endDate) pnlQuery.endDate = endDate.toISOString();
      if (bsAsOfDate) bsQuery.endDate = bsAsOfDate.toISOString();
      else if (endDate) bsQuery.endDate = endDate.toISOString();

      const [profitAndLoss, sheet] = await Promise.all([
        api.Accounting.getProfitAndLoss(pnlQuery),
        api.Accounting.getBalanceSheet(bsQuery),
      ]);
      setPnl(profitAndLoss);
      setBalanceSheet(sheet);
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!fiscalPeriodId) return;

    const initPeriod = async () => {
      setLoading(true);
      try {
        const period = await api.getById({
          apiEntity: "fiscalPeriod",
          id: fiscalPeriodId,
        });
        setPeriodName(period?.name || "");
        const periodStart = period?.startDate ? new Date(period.startDate) : null;
        const periodEnd = defaultPeriodEndDate(period?.endDate);
        setStartDate(periodStart);
        setEndDate(periodEnd);
        const nextBsAsOf = periodEnd;
        if (!bsAsOfOverrideRef.current) {
          setBsAsOfDate(nextBsAsOf);
        }

        const pnlQuery = { fiscalPeriodId };
        const bsQuery = { fiscalPeriodId };
        if (periodStart) pnlQuery.startDate = periodStart.toISOString();
        if (periodEnd) pnlQuery.endDate = periodEnd.toISOString();
        if (nextBsAsOf) bsQuery.endDate = nextBsAsOf.toISOString();
        else if (periodEnd) bsQuery.endDate = periodEnd.toISOString();

        const [profitAndLoss, sheet] = await Promise.all([
          api.Accounting.getProfitAndLoss(pnlQuery),
          api.Accounting.getBalanceSheet(bsQuery),
        ]);
        setPnl(profitAndLoss);
        setBalanceSheet(sheet);
        setLoaded(true);
      } finally {
        setLoading(false);
      }
    };

    bsAsOfOverrideRef.current = false;
    initPeriod();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fiscalPeriodId]);

  const handlePnlEndDateChange = useCallback((value) => {
    setEndDate(value);
    if (!bsAsOfOverrideRef.current) {
      setBsAsOfDate(value);
    }
  }, []);

  const handleBsAsOfDateChange = useCallback((value) => {
    bsAsOfOverrideRef.current = Boolean(value);
    setBsAsOfDate(value);
  }, []);

  const periodLabel = useMemo(() => {
    if (activeTab === "pnl") {
      if (!startDate && !endDate) return "";
      return t("accounting.financialStatements.periodRange", {
        from: formatDateLabel(startDate) || "—",
        to: formatDateLabel(endDate) || "—",
      });
    }
    const asOf = bsAsOfDate || endDate;
    return t("accounting.financialStatements.asOfLabel", {
      date: formatDateLabel(asOf) || "—",
    });
  }, [activeTab, bsAsOfDate, endDate, startDate, t]);

  const canDownloadPnl = Boolean(pnl && hasPnlActivity(pnl));
  const canDownloadBs = Boolean(balanceSheet && hasBalanceSheetActivity(balanceSheet));

  const handleDownload = useCallback(() => {
    if (activeTab === "pnl") {
      if (!canDownloadPnl) {
        showNotification({
          color: "yellow",
          title: t("accounting.financialStatements.downloadFailed"),
          message: t("accounting.financialStatements.noActivity"),
        });
        return;
      }
      downloadProfitAndLossCsv({
        report: pnl,
        periodName,
        startDate,
        endDate,
        t,
      });
      return;
    }
    if (!canDownloadBs) {
      showNotification({
        color: "yellow",
        title: t("accounting.financialStatements.downloadFailed"),
        message: t("accounting.financialStatements.noActivity"),
      });
      return;
    }
    downloadBalanceSheetCsv({
      report: balanceSheet,
      periodName,
      bsAsOfDate: bsAsOfDate || endDate,
      t,
    });
  }, [
    activeTab,
    balanceSheet,
    bsAsOfDate,
    canDownloadBs,
    canDownloadPnl,
    endDate,
    periodName,
    pnl,
    startDate,
    t,
  ]);

  return (
    <div>
      <Text size="xs" color="dimmed" mb="xs">
        {t("accounting.financeReports.financialStatements.intro")}
      </Text>
      <AccountingReportFilters
        fiscalPeriodId={fiscalPeriodId}
        onFiscalPeriodChange={setFiscalPeriodId}
        showPnlDateRange
        pnlStartDate={startDate}
        onPnlStartDateChange={setStartDate}
        pnlEndDate={endDate}
        onPnlEndDateChange={handlePnlEndDateChange}
        showBsAsOfDate
        bsAsOfDate={bsAsOfDate}
        onBsAsOfDateChange={handleBsAsOfDateChange}
      />
      {!fiscalPeriodId && !loading && (
        <Text size="sm" color="dimmed" mb="md">
          {t("accounting.financialStatements.selectPeriodHint")}
        </Text>
      )}
      <Group mb="sm">
        <Button
          size="xs"
          variant="default"
          onClick={handleDownload}
          disabled={activeTab === "pnl" ? !canDownloadPnl : !canDownloadBs}
        >
          {t("Download")}
        </Button>
        <Button size="xs" onClick={loadReports} loading={loading}>
          {t("Refresh")}
        </Button>
      </Group>
      <Tabs value={activeTab} onTabChange={setActiveTab}>
        <Tabs.List mb="md">
          <Tabs.Tab value="pnl">
            {t("accounting.financialStatements.tabPnl")}
          </Tabs.Tab>
          <Tabs.Tab value="balanceSheet">
            {t("accounting.financialStatements.tabBalanceSheet")}
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="pnl">
          <ProfitAndLossView
            report={pnl}
            loading={loading || (!loaded && Boolean(fiscalPeriodId))}
            periodLabel={periodLabel}
          />
        </Tabs.Panel>
        <Tabs.Panel value="balanceSheet">
          <BalanceSheetView
            report={balanceSheet}
            loading={loading || (!loaded && Boolean(fiscalPeriodId))}
            periodLabel={periodLabel}
          />
        </Tabs.Panel>
      </Tabs>
    </div>
  );
};

export default ReportFinancialStatements;
